import {
  Badge,
  Button,
  Card,
  Group,
  Loader,
  NumberInput,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { ArrowLeft, Check, FlaskConical } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import {
  getLabOrder,
  type LabResultEntry,
  listTestDefinitions,
  submitLabResults,
} from "@/lib/api/laboratory";

interface DraftResult {
  test_code: string;
  value_numeric: number | "";
  value_text: string;
}

export function LabResultEntryPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: order, isLoading } = useQuery({
    queryKey: ["lab-order", id],
    queryFn: () => getLabOrder(id!),
    enabled: !!id,
  });

  const { data: definitions } = useQuery({
    queryKey: ["lab-test-definitions"],
    queryFn: listTestDefinitions,
  });

  const [drafts, setDrafts] = useState<DraftResult[]>([]);

  useEffect(() => {
    if (order) {
      setDrafts(
        order.tests.map((code) => ({ test_code: code, value_numeric: "", value_text: "" })),
      );
    }
  }, [order?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const definitionsMap = new Map(definitions?.map((d) => [d.code, d]) ?? []);

  const mutation = useMutation({
    mutationFn: (results: LabResultEntry[]) => submitLabResults(id!, results),
    onSuccess: () => {
      notifications.show({ color: "blue", message: t("laboratory.saved") });
      queryClient.invalidateQueries({ queryKey: ["lab-order", id] });
      queryClient.invalidateQueries({ queryKey: ["lab-orders"] });
      navigate("/laboratory");
    },
  });

  if (isLoading || !order) return <Loader />;

  const onSubmit = () => {
    const payload: LabResultEntry[] = drafts
      .filter((d) => d.value_numeric !== "" || d.value_text)
      .map((d) => ({
        test_code: d.test_code,
        value_numeric: d.value_numeric === "" ? null : Number(d.value_numeric),
        value_text: d.value_text || null,
      }));
    if (payload.length === 0) {
      notifications.show({ color: "orange", message: t("laboratory.empty_results") });
      return;
    }
    mutation.mutate(payload);
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="center">
        <Group gap="md">
          <UnstyledButton
            onClick={() => navigate(-1)}
            style={{ color: "var(--mantine-color-gray-6)", padding: 8, borderRadius: 999 }}
          >
            <ArrowLeft size={22} strokeWidth={2} />
          </UnstyledButton>
          <Stack gap={2}>
            <Title order={3} fw={700}>
              {t("laboratory.enter_results")}
            </Title>
            <Text c="dimmed" size="sm">
              {order.tests.length} {t("laboratory.fields.tests_count")}
            </Text>
          </Stack>
        </Group>
        <Group gap="md">
          <UnstyledButton
            onClick={() => navigate(-1)}
            style={{ color: "var(--mantine-color-gray-6)", fontWeight: 500, fontSize: 14 }}
          >
            {t("common.cancel")}
          </UnstyledButton>
          <Button
            radius="xl"
            size="md"
            onClick={onSubmit}
            loading={mutation.isPending}
            leftSection={<Check size={16} strokeWidth={2.2} />}
            px={32}
            style={{ boxShadow: "0 6px 16px rgba(37, 99, 235, 0.25)" }}
          >
            {t("laboratory.submit_results")}
          </Button>
        </Group>
      </Group>

      <Card padding={0} radius="xl">
        <Group p="lg" pb={0} gap="sm">
          <ThemeIcon size={36} radius="xl" variant="light" color="brand">
            <FlaskConical size={18} strokeWidth={2} />
          </ThemeIcon>
          <Title order={5} fw={700}>
            {t("laboratory.title")}
          </Title>
        </Group>

        <Table.ScrollContainer minWidth={760}>
          <Table verticalSpacing="md" striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("laboratory.fields.test")}</Table.Th>
                <Table.Th>{t("laboratory.fields.value")}</Table.Th>
                <Table.Th>{t("laboratory.fields.text_value")}</Table.Th>
                <Table.Th>{t("laboratory.fields.unit")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {drafts.map((d, i) => {
                const defn = definitionsMap.get(d.test_code);
                const isText = !!defn?.accepted_text_values;
                return (
                  <Table.Tr key={d.test_code}>
                    <Table.Td>
                      <Stack gap={0}>
                        <Text fw={600} size="sm">
                          {defn?.name_uz ?? defn?.name_ru ?? d.test_code}
                        </Text>
                        <Text size="xs" c="dimmed" ff="monospace">
                          {d.test_code}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td style={{ width: 180 }}>
                      {!isText && (
                        <NumberInput
                          value={d.value_numeric}
                          onChange={(v) => {
                            const next = [...drafts];
                            next[i] = { ...d, value_numeric: typeof v === "number" ? v : "" };
                            setDrafts(next);
                          }}
                          decimalScale={4}
                          placeholder="0"
                        />
                      )}
                    </Table.Td>
                    <Table.Td style={{ width: 220 }}>
                      {isText && (
                        <TextInput
                          value={d.value_text}
                          placeholder="negative / positive"
                          onChange={(e) => {
                            const next = [...drafts];
                            next[i] = { ...d, value_text: e.currentTarget.value };
                            setDrafts(next);
                          }}
                        />
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color="blue" radius="sm">
                        {defn?.unit ?? "—"}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
    </Stack>
  );
}
